import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../use-is-mounted";
import Api from "../../../core/services/api/api";
import {useBizFileUrlsResolver} from "../use-biz-file-urls-resolver";
import {createUUId} from "../../../core/services/utils/utils";
import CacheService from "../../../core/services/cache/cache_service";
import LocalStorageService from "../../../core/services/cache/local_storage_service";
import {useAuthenticationState} from "../use-auth-state";

const CachedIndustryControllerContext = createContext({
    getIndustries: async () => void 0,
    setIndustryAsDefault: async () => void 0,
    getCategoriesOfIndustry: async () => void 0,
    invalidateIndustriesCache: async () => void 0,
    invalidateCategoriesCache: () => void 0,
})

export const CachedIndustryProvider = ({children}) => {
    const bizFileUrls = useBizFileUrlsResolver((t) => t.bizFile);
    const categoriesCache = useRef({});
    const industriesCache = useRef();
    const ensureDefaultInFlight = useRef(null);
    const [, setSettingDefaultIndustry] = useState(false);
    const [loadingIndustries, setLoadingIndustries] = useState(false);
    const [fetchIndustriesQueue, setFetchIndustriesQueue] = useState([]);
    const [fetchCategoriesQueue, setFetchCategoriesQueue] = useState([]);
    const [loadingCategoriesForIndustries, setLoadingCategoriesForIndustries] = useState([]);
    const processedIds = useRef([]);
    const isMounted = useIsMounted();
    const {loggedIn} = useAuthenticationState();

    /**
     * Sets the industry as default on the server and updates the in-memory industries cache.
     * @type {(function(*): Promise<boolean>)}
     */
    const setIndustryAsDefault = useCallback(async (industryId) => {
        setSettingDefaultIndustry(true);
        const response = await Api.setIndustryAsDefault(industryId);
        if (!isMounted())
            return false;
        setSettingDefaultIndustry(false);
        if (!response?.resultFlag)
            return false;
        industriesCache.current = industriesCache.current?.map(e => ({...e, isDefault: e.id === industryId})) ?? [];
        return true;
    }, [isMounted])

    /**
     * When the partner industry list has no isDefault (new access list or previous default removed),
     * persist industries[0] as default once. Shared in-flight promise prevents duplicate SetDefault calls
     * when many useIndustry instances mount together.
     * @type {(function(*): Promise<*[]>)}
     */
    const ensureHasDefaultIndustry = useCallback(async (industries) => {
        if (!industries?.length) {
            return industries ?? [];
        }
        if (industries.some((industry) => !!industry?.isDefault)) {
            return industries;
        }
        if (!ensureDefaultInFlight.current) {
            const industryId = industries[0].id;
            ensureDefaultInFlight.current = (async () => {
                const resultFlag = await setIndustryAsDefault(industryId);
                if (!resultFlag) {
                    ensureDefaultInFlight.current = null;
                    return industries;
                }
                return industriesCache.current ?? industries.map((e) => ({
                    ...e,
                    isDefault: e.id === industryId,
                }));
            })();
        }
        return await ensureDefaultInFlight.current;
    }, [setIndustryAsDefault])

    /**
     * Fetches the list of industries from the server and ensures one is marked default.
     */
    const getIndustries = useCallback(async () => {
        if (industriesCache.current) {
            return industriesCache.current;
        }
        if (!loggedIn) {
            return null; // not logged in
        }
        setLoadingIndustries(true);
        const response = await Api.getPartnerIndustries();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            // Keep loadingIndustries true until ensure finishes so the fetch queue
            // does not start another getIndustries while default is being persisted.
            industriesCache.current = await ensureHasDefaultIndustry(response?.data ?? []);
        }
        if (!isMounted())
            return;
        setLoadingIndustries(false);
        return industriesCache.current ?? response?.data;
    }, [ensureHasDefaultIndustry, isMounted, loggedIn])

    /**
     * Fetches the categories of the provided industry id
     */
    const getCategoriesOfIndustry = useCallback(async (industryId) => {
        setLoadingCategoriesForIndustries(prevState => [...prevState, industryId]);
        if (categoriesCache.current[industryId]) {
            setLoadingCategoriesForIndustries(prevState => prevState.filter(e => e !== industryId));
            return categoriesCache.current[industryId]
        }
        if (!loggedIn) {
            return null; // not logged in
        }
        const response = await Api.listPartCategoryNew(industryId);
        if (response?.resultFlag && response?.data) {
            for (const e of response.data) {
                e.coverFileName = bizFileUrls.getFilePreviewUrl(e.coverFileName)
            }
            categoriesCache.current[industryId] = response.data ?? [];
        }
        if (!isMounted())
            return;
        setLoadingCategoriesForIndustries(prevState => prevState.filter(e => e !== industryId));
        return response?.data;
    }, [isMounted, loggedIn, bizFileUrls])

    /**
     * With each change in the fetchIndustriesQueue:
     * - Fetches the industries from the server for each item in the queue.
     */
    useEffect(() => {
        if (loadingIndustries)
            return;
        const first = fetchIndustriesQueue.find(e => !processedIds.current.includes(e.id));
        if (!first)
            return;
        processedIds.current.push(first.id);
        getIndustries().then((val) => {
            setFetchIndustriesQueue(prevState => prevState.filter(e => e.id !== first.id))
            processedIds.current = processedIds.current.filter(e => e !== first.id)
            first.resolve(val);
        });
    }, [fetchIndustriesQueue, loadingIndustries, getIndustries])

    /**
     * With each change in the fetchCategoriesQueue:
     * - Fetches the categories from the server for each item in the queue.
     */
    useEffect(() => {
        const first = fetchCategoriesQueue.find(e => !processedIds.current.includes(e.id));
        if (!first)
            return;
        if (loadingCategoriesForIndustries.includes(first.industryId))
            return;
        processedIds.current.push(first.id);
        getCategoriesOfIndustry(first.industryId).then((val) => {
            if (isMounted()) {
                setFetchCategoriesQueue(prevState => prevState.filter(e => e.id !== first.id))
            }
            processedIds.current = processedIds.current.filter(e => e !== first.id)
            first.resolve(val);
        });
    }, [isMounted, fetchCategoriesQueue, loadingCategoriesForIndustries, getCategoriesOfIndustry])

    /**
     * Invalidate the cache for the categories of an industry.
     * @type {(function(*): void)}
     */
    const invalidateCategoriesCache = useCallback((industryId) => {
        if (industryId) {
            categoriesCache.current[industryId] = undefined;
        } else {
            categoriesCache.current = {};
        }
    }, [])

    /**
     * Invalidate the cache for the industries.
     * @type {(function(): void)}
     */
    const invalidateIndustriesCache = useCallback(() => {
        industriesCache.current = undefined;
        ensureDefaultInFlight.current = null;
    }, []);


    const controller = useMemo(() => ({
        getIndustries: async () => {
            return await new Promise(r => {
                setFetchIndustriesQueue(prevState => [...prevState, {resolve: r, id: createUUId()}])
            })
        },
        getCategoriesOfIndustry: async (industryId) => {
            return await new Promise(r => {
                setFetchCategoriesQueue(prevState => [...prevState, {
                    resolve: r,
                    id: createUUId(),
                    industryId: industryId,
                }])
            })
        },
        invalidateCategoriesCache: invalidateCategoriesCache,
        invalidateIndustriesCache: invalidateIndustriesCache,
        setIndustryAsDefault: setIndustryAsDefault,
    }), [invalidateCategoriesCache, invalidateIndustriesCache, setIndustryAsDefault])

    return (
        <>
            <CachedIndustryControllerContext.Provider value={controller}>
                {children}
            </CachedIndustryControllerContext.Provider>
        </>
    );
}

/**
 * Hook to manage the industry of the operator.
 */
const useIndustry = ({noCategories = false, loadAllCategories = false} = {}) => {
    const {
        setIndustryAsDefault,
        getCategoriesOfIndustry,
        getIndustries,
        invalidateCategoriesCache,
        invalidateIndustriesCache,
    } = useContext(CachedIndustryControllerContext);
    const [industries, setIndustries] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    const [cachedIndustry, setCachedIndustry] = useState();
    const [categoriesOfCachedIndustry, setCategoriesOfCachedIndustry] = useState([]);
    const [fetchedIndustry, setFetchedIndustry] = useState();
    const [categoriesOfFetchedIndustry, setCategoriesOfFetchedIndustry] = useState([]);
    const [loadingIndustries, setLoadingIndustries] = useState(true);
    const [loadingAllCategories, setLoadingAllCategories] = useState(false);
    const [loadingCachedCategories, setLoadingCachedCategories] = useState(false);
    const [loadingFetchedCategories, setLoadingFetchedCategories] = useState(false);
    const isMounted = useIsMounted();

    const hasNoIndustries = useMemo(() => !loadingIndustries && !industries.length, [industries.length, loadingIndustries]);

    /**
     * Sets the session-selected industry only. Does not persist as the operator default.
     * @type {(function(*): void)}
     */
    const selectIndustry = useCallback((industry) => {
        if (!industry) {
            return;
        }
        setCachedIndustry(industry);
    }, []);

    /**
     * Persists the industry as the operator default and syncs local industries + session selection.
     * @type {(function(*): Promise<boolean>)}
     */
    const setDefaultIndustry = useCallback(async (industry) => {
        if (!industry) {
            return false;
        }
        if (industry.isDefault) {
            setCachedIndustry(industry);
            return true;
        }
        const resultFlag = await setIndustryAsDefault(industry.id);
        if (!isMounted()) {
            return false;
        }
        if (resultFlag) {
            const updatedIndustry = {...industry, isDefault: true};
            setIndustries((prevState) => prevState.map((e) => ({
                ...e,
                isDefault: e.id === industry.id,
            })));
            setCachedIndustry(updatedIndustry);
        }
        return resultFlag;
    }, [setIndustryAsDefault, isMounted]);

    /**
     * Fetches the list of industries from the server.
     * Provider ensures a server default when none exists; this only hydrates session selection.
     * @type {(function(): Promise<void>)}
     */
    const getAllIndustries = useCallback(async () => {
        setLoadingIndustries(true);
        const industries = await getIndustries();
        if (!isMounted())
            return;
        setLoadingIndustries(false);
        if (industries) {
            setIndustries(industries ?? []);
            const defaultIndustry = industries.find((industry) => !!industry?.isDefault)
                ?? (industries.length ? industries[0] : null);
            if (defaultIndustry) {
                selectIndustry(defaultIndustry);
            }
        }
        return industries ?? [];
    }, [getIndustries, isMounted, selectIndustry])

    /**
     * Fetches the categories of the provided industries from the server.
     * @type {(function(*): Promise<void>)|*}
     */
    const getAllCategories = useCallback(async (industries) => {
        if (!loadAllCategories)
            return;
        if (!industries?.length)
            return;
        setLoadingAllCategories(true);
        const allCategories = [];
        for (const industry of industries) {
            const categories = await getCategoriesOfIndustry(industry.id);
            if (!isMounted())
                return;
            if (categories) {
                allCategories.push(...categories);
            }
        }
        setLoadingAllCategories(false);
        setAllCategories(allCategories);
    }, [getCategoriesOfIndustry, isMounted, loadAllCategories])

    /**
     * As soon as the component is mounted:
     * - Fetches the list of industries from the server.
     */
    useEffect(() => {
        getAllIndustries()
            .then((industries) => {
                if (allCategories) {
                    void getAllCategories(industries ?? []);
                }
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])


    /**
     * Fetch the industry from the server.
     * @type {(function({industryId?: string, categoryId?: number}, cache?: boolean): Promise<any>)}
     */
    const fetchIndustry = useCallback(async ({industryId}, cache = false) => {
        if (industryId) {
            const industry = industries.find((industry) => industry.id === industryId);
            if (industry) {
                setFetchedIndustry(industry);
                if (cache) {
                    selectIndustry(industry);
                }
            } else {
                setFetchedIndustry(undefined);
            }
            return industry;
        }
    }, [industries, selectIndustry])

    /**
     * Fetch categories of the cached industry.
     * @type {(function(): Promise<void>)}
     */
    const getCategoriesOfCachedIndustry = useCallback(async () => {
        if (noCategories)
            return;
        if (!cachedIndustry?.id)
            return;
        setLoadingCachedCategories(true);
        const categories = await getCategoriesOfIndustry(cachedIndustry.id);
        if (!isMounted())
            return;
        setLoadingCachedCategories(false);
        if (categories) {
            setCategoriesOfCachedIndustry(categories);
        } else {
            setCategoriesOfCachedIndustry([]);
        }
    }, [noCategories, cachedIndustry?.id, getCategoriesOfIndustry, isMounted]);

    /**
     * When the cached industry changes:
     * - Fetch categories of the cached industry.
     */
    useEffect(() => {
        getCategoriesOfCachedIndustry().then();
    }, [getCategoriesOfCachedIndustry]);

    /**
     * Fetch categories of the fetched industry.
     * @type {(function(): Promise<void>)}
     */
    const getCategoriesOfFetchedIndustry = useCallback(async () => {
        if (noCategories)
            return;
        if (!fetchedIndustry?.id)
            return;
        setLoadingFetchedCategories(true);
        const categories = await getCategoriesOfIndustry(fetchedIndustry.id);
        if (!isMounted())
            return;
        setLoadingFetchedCategories(false);
        if (categories) {
            setCategoriesOfFetchedIndustry(categories);
        } else {
            setCategoriesOfFetchedIndustry([]);
        }
    }, [noCategories, fetchedIndustry?.id, getCategoriesOfIndustry, isMounted]);

    /**
     * When the fetched industry changes:
     * - Fetch categories of the fetched industry.
     */
    useEffect(() => {
        getCategoriesOfFetchedIndustry().then();
    }, [getCategoriesOfFetchedIndustry]);


    return {
        industries,
        allCategories: allCategories,
        loadingAllCategories,
        cachedIndustry,
        selectIndustry,
        setDefaultIndustry,
        fetchedIndustry,
        fetchIndustry,
        categoriesOfCachedIndustry,
        categoriesOfFetchedIndustry,
        loadingIndustries,
        loadingCachedCategories,
        loadingFetchedCategories,
        invalidateCategoriesCache,
        invalidateIndustriesCache,
        hasNoIndustries,
    };
}


const CachedIndustrySchemaControllerContext = createContext({
    getSchemaForIndustry: async () => void 0,
    invalidateSchemaCache: () => void 0,
});

export const CachedIndustrySchemaProvider = ({children}) => {
    const [cache, setCache] = useState(CacheService.getSearchSchema() ?? {});
    const isMounted = useIsMounted();
    const {loggedIn} = useAuthenticationState();

    /**
     * As soon as the component is mounted:
     * - Listen to the storage event to update the cache.
     */
    useEffect(() => {
        const storageListener = (e) => {
            if (e.key !== LocalStorageService.keys.searchSchema)
                return;
            if (!e.newValue)
                return setCache({});
            try {
                setCache(prevState => ({
                    ...prevState,
                    ...(JSON.parse(e.newValue) ?? {}),
                }));
            } catch (e) {
            }
        }
        window.addEventListener('storage', storageListener);
        return () => window.removeEventListener('storage', storageListener);
    }, [isMounted])

    /**
     * Fetches the schema of an industry.
     * @type {(function(*): Promise<null|*|undefined>)}
     */
    const getSchemaForIndustry = useCallback(async (industryId) => {
        if (!loggedIn) {
            return null; // not logged in
        }
        if (!industryId) {
            return null; // no industry
        }
        const cached = cache[industryId];
        if (cached) {
            return cached;
        }
        const response = await Api.getNexusSearchBaseStructure(industryId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            response.data = {
                ...response.data,
                minRelevanceScore: response.data.primaryPercentile,
                entries: response.data.fields.map(e => ({
                    raw: e,
                    id: e.id,
                    name: e.name,
                    title: e.title,
                    parentId: e.parentId,
                    isFacet: e.facetable,
                    sortable: e.sortable,
                    searchable: e.searchable,
                    type: e.type,
                    dataType: e.industryPropertyTypeId,
                    nullReplacement: e.nullReplacement,
                    originalId: e.propGroupId ?? e.internalId,
                    orderIndex: e.orderIndex ?? 0,
                    industryId: e.industryId,
                    hasPreDefinedValues: e.hasPreDefinedValues,
                }))
            }
            setCache(prevState => {
                const newState = {...prevState, [industryId]: response.data,};
                CacheService.setSearchSchema(newState)
                return newState
            })
        }
        return response?.data;
    }, [isMounted, cache, loggedIn])

    /**
     * Invalidates the cache for the schema of an industry or all industries if no industry id is provided.
     * @type {(function())}
     */
    const invalidateSchemaCache = useCallback((industryId) => {
        setCache(prevState => {
            const newState = industryId ? {...prevState, [industryId]: undefined} : {};
            CacheService.setSearchSchema(newState)
            return newState
        })
    }, [])

    const controller = useMemo(() => ({
        getSchemaForIndustry: getSchemaForIndustry,
        invalidateSchemaCache: invalidateSchemaCache,
    }), [getSchemaForIndustry, invalidateSchemaCache])

    return (
        <>
            <CachedIndustrySchemaControllerContext.Provider value={controller}>
                {children}
            </CachedIndustrySchemaControllerContext.Provider>
        </>
    );
}

/**
 * Returns the cached industry schema controller
 */
export const useCachedIndustrySchemaController = () => {
    return useContext(CachedIndustrySchemaControllerContext);
}


const CachedPropertyGroupsControllerContext = createContext({
    getPropertyGroupsForIndustry: async () => void 0,
    getPropertyGroupAttributes: async () => void 0,
    invalidatePropertyGroupsCache: () => void 0,
    invalidatePropertyGroupAttributesCache: () => void 0,
});

export const CachedPropertyGroupsProvider = ({children}) => {
    const [cache, setCache] = useState(CacheService.getIndustryPropertyGroups() ?? {});
    const [attributesCache, setAttributesCache] = useState(CacheService.getIndustryPropertyGroupAttributes() ?? {});
    const isMounted = useIsMounted();
    const {loggedIn} = useAuthenticationState();

    /**
     * As soon as the component is mounted:
     * - Listen to the storage event to update the cache.
     */
    useEffect(() => {
        const storageListener = (e) => {
            if (e.key === LocalStorageService.keys.industryPropertyGroups) {
                if (!e.newValue)
                    return setCache({});
                try {
                    setCache(prevState => ({
                        ...prevState,
                        ...(JSON.parse(e.newValue) ?? {})
                    }));
                } catch (e) {
                }
            } else if (e.key === LocalStorageService.keys.industryPropertyGroupAttributes) {
                if (!e.newValue)
                    return setAttributesCache({});
                try {
                    setAttributesCache(prevState => ({
                        ...prevState,
                        ...(JSON.parse(e.newValue) ?? {})
                    }));
                } catch (e) {
                }
            }
        }
        window.addEventListener('storage', storageListener);
        return () => window.removeEventListener('storage', storageListener);
    }, [isMounted])


    /**
     * Fetches the property groups of an industry.
     * @type {(function(*): Promise<null|*|undefined>)}
     */
    const getPropertyGroupsForIndustry = useCallback(async (industryId) => {
        if (!loggedIn) {
            return null; // not logged in
        }
        if (!industryId) {
            return null; // no industry
        }
        const cached = cache[industryId];
        if (cached) {
            return cached;
        }
        const response = await Api.listIndustryGroupProperties(industryId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setCache(prevState => {
                const newState = {...prevState, [industryId]: response.data,};
                CacheService.setIndustryPropertyGroups(newState)
                return newState
            })
        }
        return response?.data;
    }, [cache, isMounted, loggedIn])

    /**
     * Fetches the property group attributes of an industry.
     * @type {(function(*): Promise<*>)}
     */
    const getPropertyGroupAttributes = useCallback(async (groupId, cancelToken) => {
        if (!loggedIn) {
            return null; // not logged in
        }
        if (!groupId) {
            return null; // no industry
        }
        const cached = attributesCache[groupId];
        if (cached) {
            return cached;
        }
        const response = await Api.listIndustryGroupPropertiesAttrs(groupId, cancelToken);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setAttributesCache(prevState => {
                const newState = {...prevState, [groupId]: response.data,};
                CacheService.setIndustryPropertyGroupAttributes(newState)
                return newState
            })
        }
        return response?.data;
    }, [attributesCache, isMounted, loggedIn])

    /**
     * Invalidates the cache for the property groups of an industry or all industries if no industry id is provided.
     * @type {(function(*): void)}
     */
    const invalidatePropertyGroupsCache = useCallback((industryId) => {
        setCache(prevState => {
            const newState = industryId ? {...prevState, [industryId]: undefined} : {};
            CacheService.setIndustryPropertyGroups(newState)
            return newState
        });
    }, [])

    /**
     * Invalidates the cache for the property group attributes of an industry or all industries if no industry id is provided.
     * @type {(function(*): void)}
     */
    const invalidatePropertyGroupAttributesCache = useCallback((groupId) => {
        setAttributesCache(prevState => {
            const newState = groupId ? {...prevState, [groupId]: undefined} : {};
            CacheService.setIndustryPropertyGroupAttributes(newState)
            return newState
        });
    }, [])

    const controller = useMemo(() => ({
        getPropertyGroupsForIndustry: getPropertyGroupsForIndustry,
        getPropertyGroupAttributes: getPropertyGroupAttributes,
        invalidatePropertyGroupsCache: invalidatePropertyGroupsCache,
        invalidatePropertyGroupAttributesCache: invalidatePropertyGroupAttributesCache,
    }), [getPropertyGroupsForIndustry, getPropertyGroupAttributes, invalidatePropertyGroupsCache, invalidatePropertyGroupAttributesCache])

    return (
        <>
            <CachedPropertyGroupsControllerContext.Provider value={controller}>
                {children}
            </CachedPropertyGroupsControllerContext.Provider>
        </>
    );
}

/**
 * Returns the cached property groups controller
 */
export const useCachedPropertyGroupsController = () => {
    return useContext(CachedPropertyGroupsControllerContext);
}

export default useIndustry;
